
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileText, Upload, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DocumentUploadProps {
  onContentExtracted: (content: string, title?: string) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ onContentExtracted }) => {
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingPost, setIsGeneratingPost] = useState(false);
  const [extractedContent, setExtractedContent] = useState<string>('');

  const extractFileContent = async (file: File): Promise<string> => {
    if (file.type === 'text/plain') {
      return await file.text();
    } else if (file.type === 'application/pdf') {
      // For PDF files - in a real implementation, you'd use a PDF parsing library
      return `Content from PDF: ${file.name}\n\n[PDF content would be extracted here using a PDF parsing library like pdf-parse or PDF.js]`;
    } else {
      // For Word documents - in a real implementation, you'd use a Word parsing library
      return `Content from Word document: ${file.name}\n\n[Word content would be extracted here using a library like mammoth.js]`;
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a document to upload');
      return;
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(uploadFile.type)) {
      toast.error('Please upload a PDF, Word document, or text file');
      return;
    }

    setIsProcessing(true);
    
    try {
      const rawContent = await extractFileContent(uploadFile);
      const extractedTitle = uploadFile.name.replace(/\.[^/.]+$/, '');

      setExtractedContent(rawContent);
      
      // Process the content to add variables and format
      const processedContent = processDocumentContent(rawContent);
      
      onContentExtracted(processedContent, extractedTitle);
      toast.success('Document content extracted and processed successfully');
      
    } catch (error) {
      console.error('Error processing document:', error);
      toast.error('Failed to process document');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAIGeneratePost = async () => {
    if (!uploadFile) {
      toast.error('Please select a document first');
      return;
    }

    setIsGeneratingPost(true);
    
    try {
      // Extract content if not already done
      let contentToEnhance = extractedContent;
      if (!contentToEnhance) {
        contentToEnhance = await extractFileContent(uploadFile);
        setExtractedContent(contentToEnhance);
      }

      const fileName = uploadFile.name.replace(/\.[^/.]+$/, '');
      
      // Generate enhanced blog post using AI
      const { data, error } = await supabase.functions.invoke('generate-content', {
        body: {
          prompt: `Transform the following document content into a professional, engaging blog post. Enhance it with:
          - A compelling title and introduction
          - Proper blog structure with headings
          - SEO-friendly content
          - Business variables where appropriate ({businessName}, {businessEmail}, {businessPhone})
          - A strong conclusion with call-to-action
          
          Original document content:
          ${contentToEnhance}
          
          Title suggestion: ${fileName}
          
          Make it comprehensive, well-structured, and engaging for readers.`
        }
      });

      if (error) throw error;

      // Use the AI-generated content
      onContentExtracted(data.content, fileName);
      toast.success('AI-enhanced blog post generated successfully!');
      
    } catch (error) {
      console.error('Error generating AI post:', error);
      toast.error('Failed to generate AI post. Please try again.');
    } finally {
      setIsGeneratingPost(false);
    }
  };

  const processDocumentContent = (rawContent: string): string => {
    // Basic content processing - add proper blog formatting
    let processedContent = rawContent.trim();
    
    // Add basic blog structure if not present
    if (!processedContent.includes('#')) {
      // Add a main heading if none exists
      processedContent = `# Blog Post Title\n\n${processedContent}`;
    }

    // Add introduction section if not present
    if (!processedContent.toLowerCase().includes('introduction') && !processedContent.toLowerCase().includes('intro')) {
      const lines = processedContent.split('\n');
      const titleLine = lines.find(line => line.startsWith('#'));
      const titleIndex = lines.findIndex(line => line.startsWith('#'));
      
      if (titleIndex >= 0) {
        lines.splice(titleIndex + 1, 0, '', '## Introduction', '', 'Welcome to {businessName}! In this article, we\'ll explore...');
        processedContent = lines.join('\n');
      }
    }

    // Add conclusion section if not present
    if (!processedContent.toLowerCase().includes('conclusion') && !processedContent.toLowerCase().includes('summary')) {
      processedContent += `\n\n## Conclusion\n\nAt {businessName}, we're committed to providing excellent service. Contact us at {businessEmail} or call {businessPhone} to learn more about our services.`;
    }

    // Replace common business terms with variables
    processedContent = processedContent
      .replace(/\b(our company|the company|our business)\b/gi, '{businessName}')
      .replace(/\b(contact us|reach out|get in touch)\b/gi, 'contact {businessName}');

    return processedContent;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Document Upload
        </CardTitle>
        <CardDescription>
          Upload a PDF, Word document, or text file to extract content for your blog post
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <Label htmlFor="document-upload">Select Document:</Label>
          <div className="flex items-center gap-3">
            <Input
              id="document-upload"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="flex-1"
              disabled={isProcessing || isGeneratingPost}
            />
            <Button 
              onClick={handleFileUpload} 
              disabled={!uploadFile || isProcessing || isGeneratingPost}
              variant="outline"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Extract
                </>
              )}
            </Button>
          </div>
          
          {uploadFile && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
              </div>
              
              <Button 
                onClick={handleAIGeneratePost} 
                disabled={isGeneratingPost || isProcessing}
                className="w-full"
              >
                {isGeneratingPost ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    AI Generating Enhanced Post...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    AI Generate Enhanced Post
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="text-sm text-muted-foreground p-3 bg-blue-50 rounded-md">
          <p className="font-medium mb-1">Two options:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Extract Content:</strong> Basic extraction with simple formatting</li>
            <li><strong>AI Generate Enhanced Post:</strong> Uses AI to create a professional, SEO-optimized blog post from your document</li>
          </ul>
          <p className="mt-2 text-xs">
            Supported formats: PDF (.pdf), Word (.doc, .docx), Plain text (.txt)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentUpload;
